import { faMagnifyingGlass } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { createFileRoute } from '@tanstack/react-router';
import { EntityOverviewTab } from '@/app/(client)/my-styles/-components/EntityOverviewTab';
import { entityDataPointsQueryOptions } from '@/app/(client)/my-styles/-hooks/useEntityDataPoints';
import {
  type EntityKindPath,
  isEntityKindPath,
  pathToKind,
  skuDetailQueryOptions,
  styleDetailQueryOptions,
  useEntityDetail,
  variantDetailQueryOptions,
} from '@/app/(client)/my-styles/-hooks/useEntityDetail';
import { EmptyState } from '@/components/ui/empty-state';
import {
  networkCompanyOptionsQueryOptions,
  styleTypeOptionsQueryOptions,
} from '@/hooks/useDropdowns';
import { stylePropertyCategoryQueryOptions } from '@/hooks/useStylePropertyOptions';
import { useTranslator } from '@/hooks/useTranslator';
import { queryClient } from '@/lib/utils/queryClient';
import { useSessionStore } from '@/store/sessionStore';

export const Route = createFileRoute(
  '/(client)/my-styles/(pages)/(detail)/$kind/$id/overview',
)({
  loader: async ({ params }) => {
    if (!isEntityKindPath(params.kind)) return;
    const kind = params.kind;
    const companyId = useSessionStore.getState().user?.companyId ?? '';
    // The parent route already warms the entity; we re-prefetch defensively
    // (no-op when the cache is fresh) plus every dropdown the General/Eco
    // sections need so the multi-selects don't pop in empty.
    const entityPrefetch =
      kind === 'styles'
        ? queryClient.ensureQueryData(styleDetailQueryOptions(params.id))
        : kind === 'variants'
          ? queryClient.ensureQueryData(variantDetailQueryOptions(params.id))
          : queryClient.ensureQueryData(skuDetailQueryOptions(params.id));
    await Promise.all([
      entityPrefetch,
      queryClient.ensureQueryData(
        entityDataPointsQueryOptions(pathToKind(kind), params.id),
      ),
      queryClient.ensureQueryData(styleTypeOptionsQueryOptions()),
      queryClient.ensureQueryData(networkCompanyOptionsQueryOptions()),
      ...(companyId
        ? (
            [
              'brands',
              'departments',
              'seasons',
              'collections',
              'customProperties',
            ] as const
          ).map(category =>
            queryClient.ensureQueryData(
              stylePropertyCategoryQueryOptions(category, companyId),
            ),
          )
        : []),
    ]);
  },
  component: EntityOverviewRoute,
});

function EntityOverviewRoute() {
  // The parent `$kind/$id/route.tsx` validates `kind` in `beforeLoad` and
  // redirects on invalid values, so by the time this renders the cast is safe.
  const { kind, id } = Route.useParams() as {
    kind: EntityKindPath;
    id: string;
  };
  const { getTranslation: t } = useTranslator({ page: 'myStyles' });
  const { data: entity } = useEntityDetail(kind, id);
  if (!entity) {
    return (
      <EmptyState
        icon={<FontAwesomeIcon icon={faMagnifyingGlass} className='size-5' />}
        title={t('detail.notFound.title')}
        description={t('detail.notFound.description')}
        className='py-16'
      />
    );
  }
  return <EntityOverviewTab kind={pathToKind(kind)} entity={entity} />;
}
